import {
    Box, Chip, Grid, InputAdornment, LinearProgress,
    List, ListItemButton, ListItemText, Paper, Button, TextField, Typography, IconButton
} from "@mui/material";
import { getCompaniesEquipment, MONITOR_COLORS } from "../../config/functions";
import { Business, Clear, Search, KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { useEffect, useRef, useState, useCallback } from "react";

const ITEMS_PER_PAGE_BASE = 6;

function Subtitle() {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, xl: 2 } }}>
            {[
                { label: 'Máq.', color: MONITOR_COLORS.equipment.machine },
                { label: 'Arm.', color: MONITOR_COLORS.equipment.locker },
                { label: 'Bal.', color: MONITOR_COLORS.equipment.counter },
            ].map(({ label, color }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: { xs: 10, xl: 14 }, height: { xs: 10, xl: 14 }, borderRadius: '3px', backgroundColor: color }} />
                    <Typography sx={{ fontSize: { xs: '0.65rem', xl: '0.75rem' }, color: MONITOR_COLORS.text.secondary, marginLeft: '1vw', marginRight: '1vw' }}>
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function CardEmpresa({ empresa }) {
    const types = [
        { label: 'Máquinas', ativo: empresa.maquinas_ativo, total: empresa.maquinas, offline: empresa.maquinas_offline, color: MONITOR_COLORS.equipment.machine },
        { label: 'Armários', ativo: empresa.armarios_ativo, total: empresa.armarios, offline: empresa.armarios_offline, color: MONITOR_COLORS.equipment.locker },
        { label: 'Balcões',  ativo: empresa.balcoes_ativo,  total: empresa.balcoes,  offline: empresa.balcoes_offline,  color: MONITOR_COLORS.equipment.counter },
    ];

    return (
        <Box sx={{
            backgroundColor: MONITOR_COLORS.secondary,
            borderRadius: 2,
            p: { xs: 1.5, xl: 2 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Business sx={{ fontSize: { xs: 14, xl: 16 }, color: MONITOR_COLORS.text.secondary, marginRight: '1vw' }} />
                <Typography sx={{ fontSize: { xs: '0.8rem', xl: '0.95rem' }, fontWeight: 600, color: MONITOR_COLORS.text.primary }}>
                    {empresa.nome}
                </Typography>
            </Box>

            {types.map(({ label, ativo, total, offline, color }) => {
                const percent = total > 0 ? (ativo / total) * 100 : 0;

                return (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: { xs: '0.65rem', xl: '0.75rem' }, color: MONITOR_COLORS.text.secondary, width: { xs: 52, xl: 62 }, flexShrink: 0 }}>
                            {label}
                        </Typography>

                        <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                                flex: 1, height: { xs: 4, xl: 6 }, borderRadius: 3,
                                backgroundColor: MONITOR_COLORS.primary,
                                '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 3 }
                            }}
                        />

                        <Typography sx={{ fontSize: { xs: '0.65rem', xl: '0.75rem' }, color: MONITOR_COLORS.text.primary, width: 30, textAlign: 'right', flexShrink: 0 }}>
                            {total > 0 ? `${ativo}/${total}` : '--'}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

export function MonitorCompanyEquipment({ empresas }) {
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [page, setPage] = useState(0);

    const searchRef = useRef(null);
    const dropdownRef = useRef(null);
    const containerRef = useRef(null);

    const listaEmpresas = empresas?.empresas ?? [];

    useEffect(() => { setPage(0); }, [selected, search]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                searchRef.current && !searchRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleEmpresa = (empresa) => {
        setSelected(prev =>
            prev.find(e => e.id_empresa === empresa.id_empresa)
                ? prev.filter(e => e.id_empresa !== empresa.id_empresa)
                : [...prev, empresa]
        );
    };

    const isSelected = (empresa) => selected.some(e => e.id_empresa === empresa.id_empresa);

    const dropdownOptions = listaEmpresas.filter(e =>
        e.nome.toLowerCase().includes(search.toLowerCase())
    );

    const filtered = selected.length > 0 ? selected : listaEmpresas;

    const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_BASE);

    useEffect(() => {
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const height = entry.contentRect.height;
                const cardHeight = window.innerHeight > 900 ? 120 : 100;

                const rows = Math.max(1, Math.floor(height / cardHeight));
                setItemsPerPage(rows * 2);
            }
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, []);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const paginated = filtered.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

    const canGoUp = page > 0;
    const canGoDown = page < totalPages - 1;

    return (
        <Box sx={{
            border: '1px solid ' + MONITOR_COLORS.tertiary,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% + 1vh)',
            marginLeft: '2vw'
        }}>
            <Box sx={{
                px: 2,
                pt: 1.5,
                pb: 1.5,
                flexShrink: 0,
                backgroundColor: MONITOR_COLORS.primary,
                borderRadius: '12px 12px 0 0',
                borderBottom: '1px solid ' + MONITOR_COLORS.tertiary,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business sx={{ color: MONITOR_COLORS.text.primary, fontSize: { xs: 18, xl: 22 }, marginRight: '1vw' }} />
                        <Typography sx={{ fontWeight: 700, color: MONITOR_COLORS.text.primary, fontSize: { xs: '0.9rem', xl: '1rem' } }}>
                            Equipamentos por Empresa
                        </Typography>
                    </Box>
                    <Subtitle />
                </Box>

                <Box sx={{ position: 'relative' }}>
                    <TextField
                        ref={searchRef}
                        fullWidth
                        size="small"
                        placeholder="Todas as Empresas"
                        value={search}
                        onChange={e => { setSearch(e.target.value); setDropdownOpen(true); }}
                        onFocus={() => setDropdownOpen(true)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: MONITOR_COLORS.text.secondary, fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <Button size="small" onClick={() => { setSearch(''); setDropdownOpen(false); }}
                                            sx={{ minWidth: 0, p: 0.5, color: MONITOR_COLORS.text.secondary }}>
                                        <Clear fontSize="small" />
                                    </Button>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: MONITOR_COLORS.text.primary,
                                backgroundColor: MONITOR_COLORS.secondary,
                                fontSize: { xs: '0.8rem', xl: '0.9rem' },
                                '& fieldset': { borderColor: MONITOR_COLORS.tertiary },
                                '&:hover fieldset': { borderColor: MONITOR_COLORS.text.secondary },
                                '&.Mui-focused fieldset': { borderColor: MONITOR_COLORS.text.primary },
                            },
                        }}
                    />

                    {dropdownOpen && dropdownOptions.length > 0 && (
                        <Paper ref={dropdownRef} sx={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                            backgroundColor: MONITOR_COLORS.text.primary,
                            border: '1px solid ' + MONITOR_COLORS.primary,
                            borderRadius: 2, mt: 0.5, maxHeight: 200, overflowY: 'auto',
                            '&::-webkit-scrollbar': { width: 4 },
                            '&::-webkit-scrollbar-thumb': { backgroundColor: MONITOR_COLORS.tertiary, borderRadius: 2 },
                        }}>
                            <List dense disablePadding>
                                {dropdownOptions.map(empresa => (
                                    <ListItemButton
                                        key={empresa.id_empresa}
                                        selected={isSelected(empresa)}
                                        onClick={() => { toggleEmpresa(empresa); setDropdownOpen(false); }}
                                        sx={{
                                            '&.Mui-selected': { backgroundColor: MONITOR_COLORS.tertiary + '55' },
                                            '&:hover': { backgroundColor: MONITOR_COLORS.tertiary + '33' },
                                        }}
                                    >
                                        <ListItemText
                                            primary={empresa.nome}
                                            primaryTypographyProps={{ fontSize: '0.8rem', color: MONITOR_COLORS.primary }}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Box>

                {selected.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {selected.map(e => (
                            <Chip key={e.id_empresa} label={e.nome} size="small" onDelete={() => toggleEmpresa(e)}
                                  sx={{
                                      backgroundColor: MONITOR_COLORS.tertiary + '55',
                                      color: MONITOR_COLORS.text.primary, fontSize: '0.7rem',
                                      '& .MuiChip-deleteIcon': { color: MONITOR_COLORS.text.secondary },
                                  }}
                            />
                        ))}
                        <Chip label="Limpar tudo" size="small" onClick={() => setSelected([])}
                              sx={{
                                  backgroundColor: 'transparent', color: MONITOR_COLORS.text.secondary,
                                  fontSize: '0.7rem', border: '1px solid ' + MONITOR_COLORS.tertiary,
                              }}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: MONITOR_COLORS.primary,
                borderRadius: '0 0 12px 12px',
                overflow: 'hidden',
                minHeight: 0,
            }}>
                <Box
                    ref={containerRef}
                    sx={{
                        flex: 1,
                        p: 1.5,
                        overflow: 'hidden',
                        minHeight: 0,
                    }}
                >
                    <Grid container spacing={1.5}>
                        {paginated.map(empresa => (
                            <Grid item xs={12} md={6} key={empresa.id_empresa}>
                                <CardEmpresa empresa={empresa} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {totalPages > 1 && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        px: 1,
                        borderLeft: '1px solid ' + MONITOR_COLORS.tertiary + '55',
                    }}>
                        <Typography sx={{
                            fontSize: '0.65rem',
                            color: MONITOR_COLORS.text.secondary,
                            fontFamily: 'monospace',
                        }}>
                            {page + 1}/{totalPages}
                        </Typography>

                        <IconButton
                            onClick={() => setPage(p => p - 1)}
                            disabled={!canGoUp}
                            size="small"
                            sx={{
                                color: canGoUp ? MONITOR_COLORS.text.primary : MONITOR_COLORS.tertiary,
                                backgroundColor: canGoUp ? MONITOR_COLORS.tertiary + '33' : 'transparent',
                                border: '1px solid ' + (canGoUp ? MONITOR_COLORS.tertiary : MONITOR_COLORS.tertiary + '44'),
                                '&:hover': {
                                    backgroundColor: canGoUp ? MONITOR_COLORS.tertiary + '66' : 'transparent',
                                },
                                transition: 'all 0.2s',
                                width: 32,
                                height: 32,
                            }}
                        >
                            <KeyboardArrowUp fontSize="small" />
                        </IconButton>

                        <IconButton
                            onClick={() => setPage(p => p + 1)}
                            disabled={!canGoDown}
                            size="small"
                            sx={{
                                color: canGoDown ? MONITOR_COLORS.text.primary : MONITOR_COLORS.tertiary,
                                backgroundColor: canGoDown ? MONITOR_COLORS.tertiary + '33' : 'transparent',
                                border: '1px solid ' + (canGoDown ? MONITOR_COLORS.tertiary : MONITOR_COLORS.tertiary + '44'),
                                '&:hover': {
                                    backgroundColor: canGoDown ? MONITOR_COLORS.tertiary + '66' : 'transparent',
                                },
                                transition: 'all 0.2s',
                                width: 32,
                                height: 32,
                            }}
                        >
                            <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
}